import 'components/ui/dropdowns/react-select-v5-theme.css'

import { MdAddToHomeScreen } from '@react-icons/all-files/md/MdAddToHomeScreen'
import lodashDebounce from 'lodash/debounce'
// should we rely on tree-shaking for lodash/flatMap etc? needs testing
import lodashFlatMap from 'lodash/flatMap'
import React, { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link, NavigateFunction } from 'react-router-dom'
import Select from 'react-select'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  ASSIGN_URL_TO_CONTACTS,
  EMAIL_CAMPAIGN_BUILDER,
  IS_ARTIST,
} from '@disco/constants/FeatureTypes'
import {
  BUSINESS,
  EMAIL,
  RECIPIENT_GROUP,
  USER,
} from '@disco/constants/RecipientTypes'
import {
  resetPlaylistShareForm,
  updatePlaylistShareForm,
} from '@disco/state-management/actions/FormActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  isFeatureEnabled,
  makeGetFeatureData,
} from '@disco/state-management/selectors/business'
import formatCommaEnumeration from '@disco/utils/formatCommaEnumeration'
import type { TrialLeft } from '@disco/utils/trial'
import { validateEmail } from '@disco/utils/validation'

import type { GroupId } from 'types/group'
import type { Playlist } from 'types/playlist'
import type { Track } from 'types/track'
import type { User } from 'types/user'

// TODO: clean-up `RecipientList`
// import RecipientList from 'components/common/email-groups/recipient-list'
import AddRecipient from 'components/common/email-groups/add-recipient'
import HelpTooltip from 'components/common/tooltip'
import { Box } from 'components/ds'
import DialogFooter from 'components/modals/dialog-footer'
import { Tab, Tabs } from 'components/ui'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Header from 'components/ui/heading'
import InlineError from 'components/ui/inline-error'
import ModalWindow from 'components/ui/modal-window'

import type { RootState } from 'store/configureStore'

import AnotherRecipient from './another-recipient'
import CurrentShares from './current-shares'
import { PasswordWarning } from './password-warning'

const MAX_FIRSTNAME_LENGTH = 30
const MAX_RECIPIENTS_LENGTH = 200

type MixpanelShare = {
  'Playlist ID'?: number
  'Track ID'?: number
  'User ID'?: number
  'Contact ID': number
}

function formatUser(user) {
  return (user.name && user.name.trim()) || user.email
}

function isEmailSharingPreferred(user) {
  // when it returns true - following should be displayed:
  //
  //    We haven’t generated links for <DISCO users>
  //    to send to them, click ‘Email playlist directly’ and
  //    enter their details.
  //
  if (user.type === BUSINESS) return true

  if (user.id) {
    // is_client - whether user is not member of any business
    // is_client make sense only for users existing on servers (with id)
    // if is_client is false then we ask user to email instead
    // TODO: finish experimental change
    // return !user.is_client
    return false
  }

  // text entry not returned from server
  // on of the basic usecases of manually assigned shares
  return false
}

function pickOneBusinessId(user) {
  const firstBusiness = user.businesses && user.businesses[0]
  if (!firstBusiness) return user
  return { ...user, business_ids: [firstBusiness.id] }
}

class ShareError extends Error {
  isShareError = true

  constructor(message: string) {
    super()
    this.message = message
  }
}

function mapStateToProps(state: RootState) {
  const getFeatureData = makeGetFeatureData()
  const {
    isTrial,
    trialLeft,
    isFeatureEnabled: assignUrlContactsEnabled,
  } = getFeatureData(state, {
    featureName: ASSIGN_URL_TO_CONTACTS,
  })
  const getArtistFeatureData = makeGetFeatureData()
  const { isFeatureEnabled: isArtist } = getArtistFeatureData(state, {
    featureName: IS_ARTIST,
  })
  const emailCampaignsEnabled = isFeatureEnabled(state, {
    featureName: EMAIL_CAMPAIGN_BUILDER,
  })

  return {
    isTrial,
    trialLeft,
    assignUrlContactsEnabled,
    isPhone: isDeviceTypePhone(state),
    isArtist,
    emailCampaignsEnabled,
  }
}

type State = {
  shareTab: 0 | 1
  // 0=email|1=manual
  sharesExpanded: boolean
  sharingState: 'none' | 'saving' | 'saved' | 'error'
  showAddAnother:
    | {
        name: string
        group: boolean
        url: string
        duplicate: boolean
      }
    | false
  template: string
  // template id
  error:
    | (Error & {
        isShareError?: boolean
      })
    | null
    | undefined
}

const mapDispatchToProps = {
  updatePlaylistShareForm,
  resetPlaylistShareForm,
}

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

type OwnProps = {
  shareType: string
  onClose: (...args: Array<any>) => any
  onShare: (...args: Array<any>) => any
  shareFunction: (...args: Array<any>) => any
  user: User
  recipients: Record<string, any>[]
  shareObject: Playlist | Track
  sharesItems: Record<string, any>[] | null | undefined
  emailTemplates: Record<string, any>[] | null | undefined
  sharesLoaded: boolean
  longTabNames: boolean
  sharing: boolean
  navigate: NavigateFunction
  sendMixpanelEvent: (...args: Array<any>) => any
  pushUINotification: (...args: Array<any>) => any
  editContactField: (...args: Array<any>) => any
  message: string
  subject: string
  territories: GroupId[] | null | undefined
  disabledShares: Record<number, boolean>
  setShareDisabled: (
    r: {
      id: number
    },
    val: boolean,
  ) => void
  initialTab?: 0 | 1
  playlistPassword?: string
  autoEmailSharedPlaylistPasswords?: boolean
}

type Props = PropsFromRedux & OwnProps

class ShareTab extends Component<Props, State> {
  state: State = {
    shareTab: 0,
    sharingState: 'none',
    sharesExpanded: false,
    showAddAnother: false,
    template: 'default',
    error: null,
  }

  componentDidMount() {
    if (this.props.initialTab !== undefined) {
      this.setState({
        shareTab: this.props.initialTab,
      })
    }
  }

  componentDidUpdate(prevProps: any) {
    if (prevProps.recipients !== this.props.recipients) {
      if (this.recipientsLength > MAX_RECIPIENTS_LENGTH) {
        mixpanel.sendPureEvent(
          `User attempted to share with more than ${MAX_RECIPIENTS_LENGTH} recipients`,
          {
            recipient_count: this.recipientsLength,
          },
        )
      }
    }
  }

  get recipientsLength() {
    const recipientsLength = this.props.recipients.reduce(
      (total, item) =>
        total + (item.type !== 'saved' ? 1 : item.recipients.length),
      0,
    )

    return recipientsLength
  }

  _handleMessageChange = lodashDebounce(message => {
    this.props.updatePlaylistShareForm({
      message,
    })
  }, 500)
  _handleSubjectChange = lodashDebounce(subject => {
    this.props.updatePlaylistShareForm({
      subject,
    })
  }, 500)
  _handleSubmit = evt => {
    evt.preventDefault()
    this.setState({
      error: null,
    })
    this.props
      .shareFunction(this.props.shareObject, this.props.recipients, {
        manual: false,
        message: this.props.message,
        subject: this.props.subject,
        template: this.state.template,
        territory_ids: this.props.territories,
      })
      .then(shareResult => {
        if (shareResult && shareResult.id) {
          return this.props.onShare(
            shareResult.id,
            this.props.recipients,
            shareResult.view_url,
          )
        } else {
          const error = new ShareError(
            'There was an error trying to share this list to these recipients. Our team has been notified. In the meantime, if you need to send the list right away, you can copy the URL and email it to the recipients manually.',
          )
          this.setState({
            error,
          })
        }
      })
      .catch(error => {
        console.error(error)
        this.setState({
          error,
        })
      })
  }
  _validateRecipient = recipient => {
    let validRecipient = recipient !== null

    if (validRecipient && (recipient.type === EMAIL || recipient.email)) {
      validRecipient = validRecipient && validateEmail(recipient.email)
    }

    // if user conneceted to multiple businesses, check if business_ids is set
    if (
      validRecipient &&
      recipient.type === USER &&
      recipient.businesses &&
      recipient.businesses.length > 1
    ) {
      validRecipient =
        validRecipient &&
        recipient.business_ids &&
        recipient.business_ids.length > 0
    }

    return validRecipient
  }
  _checkRecipientUniqueness = recipient => {
    const items = this.props.recipients.filter(item => {
      if (recipient.type === 'email') return item.email === recipient.email
      return item.id === recipient.id
    })
    return items.length === 0
  }

  _spreadRecipients(unprocessedRecipients) {
    return lodashFlatMap(unprocessedRecipients, recipient => {
      if (recipient.type === RECIPIENT_GROUP) {
        return recipient.recipients
      }

      return recipient
    })
  }

  _setEmailRecipient(nextRecipients) {
    this.props.updatePlaylistShareForm({
      recipients: this._spreadRecipients(nextRecipients),
    })
  }

  _sendMixpanelAssignShareEvent(recipients) {
    let eventContext = {}
    if (this.props.shareObject.type === 'track') {
      eventContext = {
        'Track ID': this.props.shareObject.id,
        Type: 'Track',
      }
    } else {
      eventContext = {
        'Playlist ID': this.props.shareObject.playlist_id,
        Type: 'Playlist',
      }
    }
    mixpanel.sendPureEvent('Assign share', {
      ...eventContext,
      Recipients: [...new Set(recipients.map(({ id }) => id).filter(Boolean))],
    })
  }

  async _addManualRecipient(recipient) {
    try {
      this.setState({
        sharingState: 'saving',
        error: null,
      })
      let shareResult

      // is a group
      if (recipient.recipients) {
        const urlRecipients = recipient.recipients.filter(
          r => !isEmailSharingPreferred(r),
        )
        const emailRecipients = recipient.recipients.filter(
          isEmailSharingPreferred,
        )
        // TODO: replace playlistShare with bulk
        const results = await Promise.all(
          urlRecipients.map(subRecipient =>
            this.props.shareFunction(
              this.props.shareObject,
              [pickOneBusinessId(subRecipient)],
              {
                manual: true,
                noPlaylistShareNotification: true,
                territory_ids: this.props.territories,
              },
            ),
          ),
        )

        if (results.length) {
          const resultRecipient =
            results.find(r => r.recipients).recipients[0] || {}

          this._sendMixpanelAssignShareEvent(
            results.flatMap(r => r.recipients || []),
          )

          const firstSuccessfulResult = results.find(r => r.id)
          shareResult = {
            ...results[0],
            duplicate: results.some(r => r.duplicate),
            id: firstSuccessfulResult ? firstSuccessfulResult.id : null,
            ids: lodashFlatMap(results, result =>
              result.recipients.map(r => r.id),
            ),
            emailRecipients,
            name: (resultRecipient.name || '').trim() || resultRecipient.email,
            view_url: resultRecipient.recipient_view_url,
          }
        } else {
          shareResult = {
            duplicate: false,
            id: null,
            ids: [],
            emailRecipients,
            name: '',
            view_url: null,
          }
        }

        this.props.pushUINotification({
          message: `Generated ${results.length} share ${
            results.length > 1 ? 'URLs' : 'URL'
          }!`,
          level: 'success',
        })
      } else {
        let manualRecipient = { ...recipient }

        if (
          manualRecipient.email &&
          !manualRecipient.name &&
          !validateEmail(manualRecipient.email)
        ) {
          manualRecipient = {
            ...manualRecipient,
            name: manualRecipient.email,
            email: undefined,
          }
        }

        if (isEmailSharingPreferred(manualRecipient)) {
          shareResult = {
            emailRecipients: [manualRecipient],
            recipients: undefined,
          }
        } else {
          shareResult = await this.props.shareFunction(
            this.props.shareObject,
            [pickOneBusinessId(manualRecipient)],
            {
              manual: true,
              territory_ids: this.props.territories,
            },
          )
        }

        const resultRecipient =
          (shareResult.recipients && shareResult.recipients[0]) || {}

        this._sendMixpanelAssignShareEvent([resultRecipient])

        shareResult = {
          ...shareResult,
          id: resultRecipient.id,
          name: (resultRecipient.name || '').trim() || resultRecipient.email,
          view_url: resultRecipient.recipient_view_url,
        }
      }

      const emailRecipientsError: string =
        shareResult &&
        shareResult.emailRecipients &&
        shareResult.emailRecipients.length
          ? `${formatCommaEnumeration(
              shareResult.emailRecipients.map(formatUser),
            )} ${
              shareResult.emailRecipients.length > 1
                ? 'are DISCO business inboxes'
                : 'is a DISCO business inbox'
            }. To send to them, click 'Email playlist directly' and enter their details.`
          : ''
      // bail out if it is not necessary
      if (this.state.shareTab !== 1) return
      const isGroup =
        (shareResult && shareResult.ids && shareResult.ids.length > 1) || false

      if (shareResult && shareResult.duplicate) {
        this.setState({
          sharingState: 'error',
          error: emailRecipientsError
            ? new ShareError(emailRecipientsError)
            : null,
          showAddAnother: isGroup
            ? false
            : {
                duplicate: true,
                group: isGroup,
                name: shareResult.name,
                url: shareResult.view_url,
              },
        })
      } else if (shareResult && shareResult.id) {
        this.setState({
          sharingState: 'saved',
          error: emailRecipientsError
            ? new ShareError(emailRecipientsError)
            : null,
          showAddAnother: isGroup
            ? false
            : {
                duplicate: false,
                group: isGroup,
                name: shareResult.name,
                url: shareResult.view_url,
              },
        })
      } else {
        let error

        if (emailRecipientsError) {
          error = emailRecipientsError
        } else if (shareResult) {
          error = 'Could not create a share.'
        } else {
          error = 'Failed to send. Please try again later'
        }

        this.setState({
          sharingState: 'error',
          error: error ? new ShareError(error) : null,
        })
      }
    } catch (err) {
      this.setState({
        sharingState: 'error',
        error: new ShareError('Failed to send. Please try again later'),
      })
      throw err
    }
  }

  _handleAddRecipient = recipient => {
    if (this.state.shareTab === 0) {
      this._setEmailRecipient(recipient)
    } else if (this.state.shareTab === 1) {
      this._addManualRecipient(recipient)
    }
  }
  // TODO: delete `_deleteRecipient` (unused)
  _deleteRecipient = (recipient, index) => {
    this.props.updatePlaylistShareForm({
      recipients: [
        ...this.props.recipients.slice(0, index),
        ...this.props.recipients.slice(index + 1),
      ],
    })
  }

  _renderError() {
    if (!this.state.error) return null

    if (this.state.error.isShareError) {
      return (
        <div className="Login_ValidationMessage">
          {this.state.error.toString()}
        </div>
      )
    }

    const contactUs = (
      <a
        className="Login_Link"
        href="mailto:support@disco.ac"
        target="_blank"
        rel="noopener noreferrer"
      >
        contact us
      </a>
    )
    return (
      <div className="Login_ValidationMessage">
        Sorry! That share failed. Please click the send button below again, and
        if it doesn’t work {contactUs}.
      </div>
    )
  }

  _renderSend() {
    const recipientsLength = this.recipientsLength

    if (recipientsLength && recipientsLength <= MAX_RECIPIENTS_LENGTH) {
      const recipientPlural = recipientsLength > 1 ? 'Recipients' : 'Recipient'
      const caption = `Send to ${recipientsLength} ${recipientPlural}`
      const progressCaption = `Sending to ${recipientsLength} ${recipientPlural}`
      return (
        <Button
          appearance="primary"
          className="SendPlaylist_Button"
          progressIndicator={this.props.sharing}
          progressCaption={progressCaption}
          onClick={this._handleSubmit}
          caption={caption}
        />
      )
    }
  }

  _isRecipientInvalid = recipient => {
    if (this.state.shareTab === 1) {
      if (
        recipient.email &&
        recipient.email.length > 0 &&
        !validateEmail(recipient.email) && // `email` will be passed as `name`
        recipient.email.length > MAX_FIRSTNAME_LENGTH
      ) {
        // Too long name - we have to show the reason using setState.
        // Otherwise cryptic error "Failed to send" would be displayed
        // https://app.asana.com/0/364097350002545/346660564049036/f
        return 'Sorry! Maximum length for names is 30 characters'
      }

      // user can type whatever they want
      return !(
        (recipient.email && recipient.email.length > 0) || // email-less user from autocomplete
        recipient.id || // groups are fine too
        (recipient.recipients && recipient.recipients.length)
      )
    } else if (this.state.shareTab === 0) {
      return !(
        this._validateRecipient(recipient) &&
        this._checkRecipientUniqueness(recipient)
      )
    }
  }
  _handleChangeShareTab = (shareTab: 0 | 1) => {
    this.setState({
      shareTab,
      sharingState: 'none',
      error: null,
    })
  }
  _handleExpandShares = () => {
    this.setState({
      sharesExpanded: true,
    })
  }
  _handleCollapseShares = () => {
    this.setState({
      sharesExpanded: false,
    })
  }
  _handleCancelShare = async (recipient, disable) => {
    const setShareDisabled = val => this.props.setShareDisabled(recipient, val)

    if (disable) {
      setShareDisabled(disable)

      try {
        await api.disableShare(recipient.share_id)
        this.props.pushUINotification({
          message: 'Link disabled!',
          level: 'success',
        })
      } catch (err) {
        this.props.pushUINotification({
          message: 'Sorry! Failed to disable link.',
          level: 'error',
        })
        setShareDisabled(!disable)
        console.error(err)
      }
    } else {
      setShareDisabled(disable)

      try {
        await api.enablePlaylistShare(recipient.share_id)
        this.props.pushUINotification({
          message: 'Link re-enabled!',
          level: 'success',
        })
      } catch (err) {
        this.props.pushUINotification({
          message: 'Sorry! Failed to re-enable link.',
          level: 'error',
        })
        setShareDisabled(!disable)
        console.error(err)
      }
    }
  }
  _handleUserClick = recipient => {
    if (!recipient.contact) return
    if (recipient.contact.id) {
      window.open(`/contacts/${recipient.contact.id}`, '_blank')
    } else {
      window.open(`/contacts/${recipient.contact}`, '_blank')
    }
    this.props.sendMixpanelEvent('Access Contacts via share list contact')
  }
  _handleSuccessfulCopyUrl = recipient => {
    this.props.pushUINotification({
      message: 'Copied share URL!',
      level: 'success',
    })
    let eventContext = {}
    if (this.props.shareObject.type === 'track') {
      eventContext = {
        'Track ID': this.props.shareObject.id,
      }
    } else {
      eventContext = {
        'Playlist ID': this.props.shareObject.playlist_id,
      }
    }
    const mixpanelProps: MixpanelShare = {
      ...eventContext,
      'Contact ID': recipient.contact.id,
    }

    if (recipient.id !== null) {
      mixpanelProps['User ID'] = recipient.id
    }

    mixpanel.sendPureEvent('Copy assign share url', mixpanelProps)
  }
  _handleErrorCopyUrl = () => {
    this.props.pushUINotification({
      message: 'Could not copy share URL!',
      level: 'error',
    })
  }
  _handleOpenStats = () => {
    let link
    if (this.props.shareObject.type === 'track') {
      link = `/explorer/tracks/${this.props.shareObject.id}/stats`
    } else {
      link = `/explorer/playlists/${this.props.shareObject.playlist_id}/stats`
    }
    this.props.navigate(link)
    this.props.sendMixpanelEvent(
      `Access ${this.props.shareType} stats via share modal`,
    )
  }

  _defaultSubject() {
    const { first_name, last_name } = this.props.user
    const businessName = this.props.user.business.name
    let title = this.props.shareObject.name || 'untitled playlist'
    if (this.props.shareType === 'track') {
      title = this.props.shareObject.name || 'untitled track'
    }
    let fromBusinessName = `from ${businessName} `

    if (this.props.user.business.hide_business_name) {
      fromBusinessName = ''
    }

    return `${first_name} ${last_name} ${fromBusinessName}has sent you ${title}`
  }

  _renderCurrentShares() {
    return (
      <CurrentShares
        sharesItems={this.props.sharesItems || []}
        disabledShares={this.props.disabledShares}
        onSuccessfulCopyUrl={this._handleSuccessfulCopyUrl}
        onErrorCopyUrl={this._handleErrorCopyUrl}
        onUserClick={this._handleUserClick}
        onEditField={this.props.editContactField}
        onStatsClick={this._handleOpenStats}
        sharesExpanded={this.state.sharesExpanded}
        onExpandShares={this._handleExpandShares}
        onCancelShare={this._handleCancelShare}
      />
    )
  }

  _renderStats() {
    const urls = <a onClick={this._handleExpandShares}>Share URLs</a>
    const stats = <a onClick={this._handleOpenStats}>Access Stats</a>
    return (
      <div className="PlaylistShareDialog_StatsCaption">
        {!this.props.sharesLoaded
          ? 'Loading...'
          : this.props.sharesItems &&
            this.props.sharesItems.length > 0 &&
            (this.state.shareTab === 0 || this.props.isPhone ? (
              <span>
                View current {urls} and {stats}.
              </span>
            ) : (
              <span>View {stats}.</span>
            ))}
      </div>
    )
  }

  _renderFooter() {
    return (
      <React.Fragment>
        {this.state.sharesExpanded && (
          <ModalWindow
            header={<Header size="700">Current share URLs</Header>}
            onClose={this._handleCollapseShares}
            isOpen
          >
            {this._renderCurrentShares()}
          </ModalWindow>
        )}
        <DialogFooter
          primary={this._renderSend()}
          onClose={this.props.onClose}
          corner={!this.props.isPhone ? this._renderStats() : null}
        />
      </React.Fragment>
    )
  }

  _renderCustomEmailTemplateSelector() {
    const emailTemplates = this.props.emailTemplates
    if (!emailTemplates) return null
    if (emailTemplates.length < 2) return null
    return (
      <div className="PlaylistShareDialog_TemplateSelector">
        <div className="Form_Label">Choose email template</div>
        <div className="row">
          <div className="col-xs-12 col-sm-9">
            <Select
              classNamePrefix="Select"
              getOptionValue={t => String(t.id)}
              getOptionLabel={t => t.name}
              isClearable={false}
              options={emailTemplates}
              // SLAM-1969: react-select v5 selects by option object, not v1's
              // `simpleValue` id, so resolve the stored id to its template.
              value={
                emailTemplates.find(
                  t => String(t.id) === String(this.state.template),
                ) ?? null
              }
              onChange={option =>
                this.setState({
                  template: option ? option.id : undefined,
                })
              }
            />
          </div>
        </div>
      </div>
    )
  }

  _renderAddRecipient() {
    const { shareTab, showAddAnother } = this.state
    const { emailCampaignsEnabled } = this.props

    if (showAddAnother && shareTab === 1) {
      let objectId
      if (this.props.shareObject.type === 'playlist') {
        objectId = this.props.shareObject.playlist_id
      } else {
        objectId = this.props.shareObject.id
      }
      return (
        <AnotherRecipient
          objectId={objectId}
          objectType={this.props.shareType}
          showAddAnother={showAddAnother}
          sendMixpanelEvent={this.props.sendMixpanelEvent}
          onAddAnotherClick={() =>
            this.setState({
              showAddAnother: false,
            })
          }
        />
      )
    }

    const maxRecipientError = !emailCampaignsEnabled
      ? 'please reduce your recipient list to under 200'
      : 'if you need to share with more than 200 people, please use Email Creator instead'

    return (
      <>
        <AddRecipient
          key={shareTab}
          value={shareTab === 0 ? this.props.recipients : null}
          placeholder={
            shareTab === 0
              ? 'Email address or DISCO user or business'
              : 'Email address or DISCO user or name'
          }
          noBusinessChoice={shareTab === 1}
          includeBlankEmails={shareTab === 1}
          hideBusinesses={shareTab === 1}
          onAddRecipient={this._handleAddRecipient}
          isRecipientInvalid={this._isRecipientInvalid}
          csrfToken={this.props.user.csrfToken}
          isTrial={this.props.isTrial}
          assignUrlContactsEnabled={this.props.assignUrlContactsEnabled}
        />
        {this.recipientsLength > MAX_RECIPIENTS_LENGTH && (
          <InlineError
            error={`You're sharing with ${this.recipientsLength} recipients. Playlist sharing is intended for small groups – ${maxRecipientError}`}
          />
        )}
      </>
    )
  }

  _renderAlert() {
    const { trialLeft } = this.props
    if (!trialLeft) return null
    return (
      <Alert>
        Heads up! {trialLeft.message}
        <br />
        Assigned URLs are available on&nbsp;
        <Link to="/settings/billing">Plus and Pro plans</Link>
      </Alert>
    )
  }

  render() {
    const { shareTab } = this.state
    const {
      longTabNames,
      isTrial,
      isArtist,
      shareType,
      playlistPassword,
      autoEmailSharedPlaylistPasswords,
    } = this.props
    return (
      <>
        <PasswordWarning
          isPlaylist={shareType === 'playlist'}
          playlistPassword={playlistPassword}
          autoEmailSharedPlaylistPasswords={autoEmailSharedPlaylistPasswords}
          isEmailTab={shareTab === 0}
        />
        <Tabs
          className="Tabs--blue-back PlaylistShareDialog_ShareTabs"
          index={shareTab}
          onChange={this._handleChangeShareTab}
        >
          <Tab label={`Email ${shareType}`}>
            <Box mb="4">{this._renderAddRecipient()}</Box>

            {this._renderCustomEmailTemplateSelector()}

            <Box mb="4">
              <div className="Form_Label">Subject</div>
              <input
                className="Form_Input"
                onChange={e => this._handleSubjectChange(e.target.value)}
                placeholder="Enter your email subject"
                defaultValue={this.props.subject || this._defaultSubject()}
              />
            </Box>

            <Box mb="4">
              <div className="Form_Label">Message</div>
              <textarea
                id="EmailForm_MessageInput"
                placeholder="Message"
                onInput={(e: React.FormEvent<HTMLTextAreaElement>) =>
                  this._handleMessageChange(e.currentTarget.value)
                } // onInput instead of onChange should be used, so TourRunner can fill the form
                className="Form_TextArea"
                defaultValue={this.props.message}
              />
            </Box>

            {this.props.isPhone ? (
              <Box mb="4">{this._renderStats()}</Box>
            ) : null}
            {this._renderError()}

            {this._renderFooter()}
          </Tab>
          <Tab
            label={
              <span>
                {longTabNames ? (
                  <span>Assign a URL to a contact</span>
                ) : (
                  <span>Assign a URL</span>
                )}
                <HelpTooltip id="tooltip_share_assign_tab" />
              </span>
            }
          >
            <div className="PlaylistShareDialog_ShareTab">
              <Box mb="4">{this._renderAddRecipient()}</Box>
              {this.props.isPhone ? (
                <Box mb="4">{this._renderStats()}</Box>
              ) : null}
              {this._renderCustomEmailTemplateSelector()}
              {this._renderError()}
              {!this.props.isPhone ? this._renderCurrentShares() : null}
              {this._renderFooter()}
              {isTrial && !isArtist && this._renderAlert()}
            </div>
          </Tab>
        </Tabs>
      </>
    )
  }
}

export default connector(ShareTab)
